// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { CaretLeft } from "@phosphor-icons/react";
import { Badge, Button, Text } from "@radix-ui/themes";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentsList } from "./AskV2ReviewCommentsList";
import { AskV2ReviewCompareChangesColumn } from "./AskV2ReviewCompareChangesColumn";
import { AskV2ReviewComposerProvider } from "./AskV2ReviewComposerContext";
import { AskV2ReviewLiveFindings } from "./AskV2ReviewLiveFindings";
import { AskV2ReviewPreviouslyRaised } from "./AskV2ReviewPreviouslyRaised";
import { AskV2ReviewStillReviewing } from "./AskV2ReviewStillReviewing";
import { type RejectionScope } from "./rememberedSentence";
import { type CommentWithOptionalTags } from "./reviewCommentTags";
import {
  type useAskPunttReview,
  useReopenAskPunttReviewResolved,
} from "./useAskPunttReview";
import { type useAskV2ReviewCommentFilter } from "./useAskV2ReviewCommentFilter";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { type useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";
import { type useLoadedReviewCommentGroups } from "./useLoadedReviewCommentGroups";

import {
  AskPunttArtifactProvider,
  type AskPunttArtifactContextValue,
  type useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { type mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { useAutoScroll } from "../../utils/useAutoScroll";
import { TextSelectionToolbar } from "../transport/TextSelectionToolbar";
import { AskV2Thread } from "../transport/Thread";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type Review = NonNullable<
  NonNullable<ReturnType<typeof useAskPunttReview>["data"]>["review"]
>;

type FeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

type AskV2ReviewLeftColumnProps = {
  review: Review;
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  commentFilter: ReturnType<typeof useAskV2ReviewCommentFilter>;
  panelComments: readonly CommentWithOptionalTags[];
  commentNumbers: Record<string, number>;
  loadedGroups: ReturnType<typeof useLoadedReviewCommentGroups>;
  artworkFiles: ReturnType<typeof mapReviewFilesToAssets>;
  contextFiles: ReturnType<typeof mapReviewFilesToAssets>;
  artworkPaneRef: ReturnType<typeof useReviewArtworkFocus>["artworkPaneRef"];
  selectedCommentId: string | null;
  selectComment: (commentId: string) => void;
  clearComment: () => void;
  onRemoveCommentContext: (id: string) => void;
  judgmentFailed: boolean;
  judgmentPending: boolean;
  onJudge: (
    commentId: string,
    liked: boolean | null,
    scope?: RejectionScope,
  ) => Promise<void>;
  feedPresentation: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  artifactContext?: AskPunttArtifactContextValue;
  composer?: ReactNode;
  readOnly: boolean;
  /** Leaves the review for the chat it was opened from. */
  onBack: () => void;
};

export function AskV2ReviewLeftColumn({
  review,
  workspace,
  transport,
  compare,
  commentFilter,
  panelComments,
  commentNumbers,
  loadedGroups,
  artworkFiles,
  contextFiles,
  artworkPaneRef,
  selectedCommentId,
  selectComment,
  clearComment,
  onRemoveCommentContext,
  judgmentFailed,
  judgmentPending,
  onJudge,
  feedPresentation,
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  authorNameByUserId,
  artifactContext,
  composer,
  readOnly,
  onBack,
}: AskV2ReviewLeftColumnProps) {
  const { t } = useTranslation("ui");
  const { canCompare, changedRegions } = compare;
  // why: while a comparison shows its changes the column is about them only;
  // the findings, the live-review banners and the pane tabs come back on exit.
  const changesColumn = compare.showChanges ? changedRegions : undefined;
  // A fixed comment "View comment" asked for, shown in Previously raised.
  const [revealResolvedId, setRevealResolvedId] = useState<string | null>(null);
  const commentsPane = !changesColumn && workspace.leftPane === "comments";
  const reopenResolved = useReopenAskPunttReviewResolved();

  // Whether the turn behind this review is still running. The backend keeps
  // confirming/clearing it on the review itself (`aiRunning`), and the query
  // polls while it is set, so this flips to false on its own within a poll of
  // the turn ending.
  const isReviewing = review.aiRunning === true;

  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  const focusedCommentIdRef = useRef<string | null>(null);
  // why: the comment row takes the click, so the caret has to move into the
  // composer after selection or the user still has to click the field.
  useEffect(() => {
    if (!selectedCommentId) {
      focusedCommentIdRef.current = null;
      return;
    }
    if (focusedCommentIdRef.current === selectedCommentId) return;
    focusedCommentIdRef.current = selectedCommentId;
    setComposerFocusNonce((current) => current + 1);
  }, [selectedCommentId]);

  const {
    scrollContainerRef: feedScrollContainerRef,
    contentRef: feedContentRef,
    handleScroll: handleFeedScroll,
  } = useAutoScroll(
    // why: the feed container is mounted only on the feed pane, but the
    // composer that sends into it is on both. Without leftPane here, a send
    // made from the comments pane arms the pin against a container that does
    // not exist yet, and nothing re-runs the effect when the pane switches —
    // so the feed opens at the top, on the oldest messages.
    [transport.displayMessages, workspace.leftPane],
    transport.chatId,
    {
      enabled: !transport.inFlight,
      // why: the sent turn can render while the sticky scroll guard is still
      // latched, and nothing in ask-v2 clears it on send — so without an
      // explicit send signal the turn lands below the fold and the reader has
      // to scroll to find it. (The `enabled` hold is not the blocker on that
      // first commit: send() writes `sending` and the optimistic row in one
      // mutate and does not recompute `inFlight`.)
      sentSignal: transport.sending,
    },
  );

  return (
    <div className="ask-puntt-surface flex h-full min-h-0 min-w-0 flex-col">
      <div
        // why: h-12 is PANE_HEADER_HEIGHT_CLASS; ask-v2 cannot import the V1 module that exports it
        className="flex h-12 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-2.5"
        data-testid="ask-v2-review-left-header"
      >
        <Button
          variant="ghost"
          color="gray"
          size="1"
          data-testid="output-back-button"
          aria-label={t("askV2.review.backToChat")}
          onClick={onBack}
        >
          <CaretLeft size={14} weight="bold" aria-hidden="true" />
          {t("askV2.review.back")}
        </Button>
        {changesColumn ? (
          <h2
            className="m-0 flex items-center gap-2 text-[14px] font-semibold text-puntt-neutral-gray-12"
            data-testid="ask-v2-review-compare-changes-heading"
          >
            {t("askV2.review.compareChangesHeading")}
            <Badge size="1" variant="soft" color="blue">
              {changesColumn.length}
            </Badge>
          </h2>
        ) : null}
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {commentsPane && judgmentFailed ? (
          <Text
            as="p"
            size="2"
            color="red"
            role="alert"
            className="px-4 pt-3"
            data-testid="ask-v2-review-judgment-error"
          >
            {t("assetViewer.judgmentSaveFailed")}
          </Text>
        ) : null}
        {commentsPane && isReviewing ? (
          <AskV2ReviewStillReviewing
            historyMessages={transport.historyMessages}
          />
        ) : null}
        {commentsPane && isReviewing ? (
          <AskV2ReviewLiveFindings
            findings={transport.streamSnapshot?.verifiedFindings}
            // why: the join must be built from the comments the panel
            // below actually draws, not every comment christo served.
            // Built from `review.comments`, a finding a tag filter hides
            // kept its openable "Found so far" row: the click ran
            // `selectComment` and jumped the artwork pane while no card
            // existed in the list to land on. A finding she rejected
            // mid-turn is drawn (grayed), so its row stays openable.
            comments={panelComments}
            onOpenComment={selectComment}
            // why: the live row judges through the workspace's own
            // handler, not a direct mutation -- so a candidate rejected
            // mid-turn asks for a scope and writes the same
            // `liked:false` + `judgmentScope` a finished card does.
            judgmentPending={judgmentPending}
            onJudge={readOnly ? undefined : onJudge}
          />
        ) : null}
        {changesColumn ? (
          <AskV2ReviewCompareChangesColumn
            review={review}
            compare={compare}
            regions={changesColumn}
            artworkPaneRef={artworkPaneRef}
            readOnly={readOnly}
            onViewFix={(commentId) => {
              compare.setComparing(false);
              workspace.setLeftPane("comments");
              setRevealResolvedId(commentId);
            }}
          />
        ) : commentsPane ? (
          <AskV2ReviewCommentsList
            comments={panelComments}
            commentNumbers={commentNumbers}
            // why: the list unmounts on a pane switch; its own snapshot
            // would then file a card judged here into its new section.
            commentGroups={loadedGroups}
            contextFiles={contextFiles}
            reviewFiles={artworkFiles}
            selectedCommentId={selectedCommentId}
            authorNameByUserId={authorNameByUserId}
            currentUserId={currentUserId}
            currentUserName={feedActorName}
            activeTags={commentFilter.activeTags}
            showTagFilter={commentFilter.showTagFilter}
            onToggleTag={commentFilter.toggleTag}
            sourceFilter={commentFilter.sourceFilter}
            judgmentPending={judgmentPending}
            // why: the judge controls POST `updateReviewComment` outside the
            // transport, so the page's gate cannot reach them. Withholding
            // the handler is how the list stops drawing them.
            onJudge={readOnly ? undefined : onJudge}
            onSelect={selectComment}
            onClear={clearComment}
            afterUnresolved={
              <AskV2ReviewPreviouslyRaised
                resolved={review.resolved ?? []}
                versionNumber={review.versionNumber}
                onViewChange={
                  canCompare ? compare.viewResolvedChange : undefined
                }
                onReopen={
                  readOnly
                    ? undefined
                    : (commentId) =>
                        reopenResolved.mutate({
                          reviewId: review.reviewId,
                          commentId,
                          reopened: true,
                        })
                }
                reopenPending={reopenResolved.isPending}
                revealCommentId={revealResolvedId}
              />
            }
          />
        ) : (
          <div
            ref={feedScrollContainerRef}
            onScroll={handleFeedScroll}
            className="relative min-h-0 flex-1 overflow-y-auto px-4 py-6"
            data-testid="ask-v2-review-feed-scroll"
            // why: a memory chip's card keeps inside this column, scrolling yet or not, so a short feed never lets it slide over the artwork pane (`popoverBoundary`).
            data-popover-boundary
          >
            <TextSelectionToolbar
              containerRef={feedScrollContainerRef}
              onAddContext={transport.addQuotedContext}
            />
            <div ref={feedContentRef}>
              {artifactContext ? (
                <AskPunttArtifactProvider value={artifactContext}>
                  <AskV2Thread
                    transport={transport}
                    presentation={feedPresentation}
                    feedActorName={feedActorName}
                    feedActorAvatar={feedActorAvatar}
                    feedMembers={feedMembers}
                    currentUserId={currentUserId}
                    readOnly={readOnly}
                  />
                </AskPunttArtifactProvider>
              ) : (
                <AskV2Thread
                  transport={transport}
                  presentation={feedPresentation}
                  feedActorName={feedActorName}
                  feedActorAvatar={feedActorAvatar}
                  feedMembers={feedMembers}
                  currentUserId={currentUserId}
                  readOnly={readOnly}
                />
              )}
            </div>
          </div>
        )}
        {composer ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-[linear-gradient(to_top,var(--ask-puntt-surface-bg)_15%,transparent)]"
            data-testid="ask-v2-review-comment-list-fade"
          />
        ) : null}
      </div>
      {composer ? (
        <div className="shrink-0 p-4" data-testid="ask-v2-review-composer">
          <AskV2ReviewComposerProvider
            onRemoveCommentContext={onRemoveCommentContext}
            leftPane={workspace.leftPane}
            commentCount={panelComments.length}
            onPaneChange={changesColumn ? undefined : workspace.setLeftPane}
            composerFocusNonce={composerFocusNonce}
          >
            {composer}
          </AskV2ReviewComposerProvider>
        </div>
      ) : null}
    </div>
  );
}
